import { useContext } from 'react'

import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Button, Form, SidebarModal, ModalContext,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCampaignContact, { useCampaignContactForm } from '@hooks/useCampaignContact'
import useCampaignContacts from '@hooks/useCampaignContacts'
import useContact from '@hooks/useContact'

import { ContactModel } from '@models/contact'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import ContactItem from './ContactItem'

const MODAL_KEY = 'CreateCampaignContactModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignContact: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateCampaignContactModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignContact } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignContact: initCampaignContact } = modalPayload

  const { campaign_id: campaignId } = initCampaignContact

  const campaignContactPayload = useCampaignContact(initCampaignContact)

  const {
    campaignContact,
    callbacks: { createCampaignContact: createFn, createWithContact },
    creating,
  } = campaignContactPayload

  const campaignContactsPayload = useCampaignContacts({
    filters: { campaign_id: campaignId },
    performHttpRequests: !!campaignId,
    requestOptions: defaultRequestOptions.campaignContact,
  })

  const { filteredCampaignContacts } = campaignContactsPayload

  const {
    entityState,
    entityState: {
      contact_id,
    },
    errors,
    saveEnabled,
    setEntityState,
  } = useCampaignContactForm(campaignContact, { validateOn: 'change' })

  const {
    callbacks: { createOrEditContact },
  } = useContact()

  const createCampaignContactPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.campaignContact,
    toastText: 'Contact Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Contact" />

      <SidebarModal.Content>
        <Form>
          {!contact_id && (
            <>
              <EntitySelector
                callbacks={{
                  // @ts-expect-error TS2322
                  selectItem: (contact: ContactModel) => {
                    setEntityState({
                      contact_id: contact?.id,
                      contact_type: contact?.contact_type,
                    })
                  },
                }}
                entityKey="contacts"
                errorMessage={errors.contact_id}
                label="Search For Existing Contact"
                selectedIds={filteredCampaignContacts.map(campaignContactItem => campaignContactItem.contact_id)}
              />

              <Form.Field label="More Options" marginTop="large">
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => {
                    closeModal()
                    createOrEditContact({ callbacks: { createFn: createWithContact } })
                  }}
                  size="medium"
                >
                  Add New Contact
                </Button>
              </Form.Field>
            </>
          )}

          {contact_id && (
            <Form.Field label="Add Contact" labelProps={{ marginBottom: 'none' }}>
              <ContactItem
                callbacks={{ removeContact: () => setEntityState({ contact_id: undefined }) }}
                contactId={contact_id}
              />
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => createCampaignContact(createCampaignContactPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Contact' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateCampaignContactModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
